import { useEffect, useRef, useState } from "react";

import { generateCollapsedText } from "@similarweb/ui-components";

import { StyledPartialText, StyledPartialTextCollapsable, StyledExpandCollapseButton } from "./styles";

type PartialTextProps = {
    height: number;
    text: string;
    onShowMoreClick?: () => void;
    renderToggle?: (isCollapsed: boolean, onToggle: () => void) => React.ReactNode;
    isInlineToggle?: boolean;
};

const SHOW_MORE_TEXT = `...show\u00A0more`;
const SHOW_LESS_TEXT = ` show\u00A0less`;

const PartialText = ({ height, text, onShowMoreClick, renderToggle, isInlineToggle = true }: PartialTextProps) => {
    const elementRef = useRef<HTMLDivElement>(null);
    const [isFirstRender, setIsFirstRender] = useState(true);
    const [shouldBeCollapsed, setShouldBeCollapsed] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [collapsedText, setCollapsedText] = useState("");

    useEffect(() => {
        if (isFirstRender) {
            if (elementRef.current && elementRef.current.scrollHeight > elementRef.current.clientHeight) {
                setShouldBeCollapsed(true);
                const updatedCollapsedText = generateCollapsedText(
                    elementRef.current,
                    elementRef.current?.textContent || "",
                );
                const reservedLength = isInlineToggle ? SHOW_MORE_TEXT.length : 0;
                setCollapsedText(updatedCollapsedText.substring(0, updatedCollapsedText.length - reservedLength - 10));
            }
            setIsFirstRender(false);
        }
    }, [isFirstRender]);

    const handleToggle = () => {
        if (isCollapsed) {
            onShowMoreClick?.();
            setIsCollapsed(false);
        } else {
            setIsCollapsed(true);
        }
    };

    const defaultToggle = (
        <StyledExpandCollapseButton onClick={handleToggle}>
            {isCollapsed ? SHOW_MORE_TEXT : SHOW_LESS_TEXT}
        </StyledExpandCollapseButton>
    );

    if (isFirstRender) {
        return (
            <StyledPartialText
                ref={elementRef}
                height={height}
            >
                <span>{text}</span>
            </StyledPartialText>
        );
    }

    if (!shouldBeCollapsed) {
        return <span>{text}</span>;
    }

    const toggleElement = renderToggle ? renderToggle(isCollapsed, handleToggle) : defaultToggle;

    return isInlineToggle ? (
        <StyledPartialTextCollapsable
            height={height}
            isCollapsed={isCollapsed}
        >
            <span>
                {isCollapsed ? collapsedText : text}
                {toggleElement}
            </span>
        </StyledPartialTextCollapsable>
    ) : (
        <>
            <StyledPartialTextCollapsable
                ref={elementRef}
                height={height}
                isCollapsed={isCollapsed}
            >
                <span>{isCollapsed ? `${collapsedText}...` : text}</span>
            </StyledPartialTextCollapsable>
            {toggleElement}
        </>
    );
};

export default PartialText;
